import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

// Pages
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import { DashboardPage } from "./pages/DashboardPage";
import { QuotesPage, CreateQuotePage, EditQuotePage, ViewQuotePage } from "./pages/QuotesPages";
import { InvoicesPage, CreateInvoicePage, EditInvoicePage, ViewInvoicePage } from "./pages/InvoicesPages";
import { ExpensesPage, CreateExpensePage, EditExpensePage } from "./pages/ExpensesPages";
import { CommissionMapPage } from "./pages/CommissionMapPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AdminPage } from "./pages/AdminPage";
import { PublicInvoicePage } from "./pages/PublicInvoicePage";
import { ReportsPage } from "./pages/ReportsPage";
import { OrdersPage } from "./pages/OrdersPage";
import { CompletedOrdersPage } from "./pages/CompletedOrdersPage";

// Layout
import { DesktopShell } from "./components/win98/DesktopShell";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse space-y-4 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse space-y-4 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Public Invoice Payment Page */}
      <Route path="/pay/:id" element={<PublicInvoicePage />} />

      {/* Protected Dashboard Routes */}
      <Route
        element={
          <ProtectedRoute>
            <DesktopShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        
        {/* Quotes */}
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/quotes/new" element={<CreateQuotePage />} />
        <Route path="/quotes/:id" element={<ViewQuotePage />} />
        <Route path="/quotes/:id/edit" element={<EditQuotePage />} />
        
        {/* Invoices */}
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/new" element={<CreateInvoicePage />} />
        <Route path="/invoices/:id" element={<ViewInvoicePage />} />
        <Route path="/invoices/:id/edit" element={<EditInvoicePage />} />

        {/* Opened from the ntfy notification when an order needs its print folder mapped */}
        <Route path="/commissions/map/:invoiceId" element={<CommissionMapPage />} />
        
        {/* Expenses */}
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/expenses/new" element={<CreateExpensePage />} />
        <Route path="/expenses/:id/edit" element={<EditExpensePage />} />

        {/* Reports */}
        <Route path="/reports" element={<ReportsPage />} />

        {/* Orders */}
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/completed" element={<CompletedOrdersPage />} />

        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        
        {/* Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                borderRadius: 0,
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
